import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AdminPageLayout, Section } from '../../components';
import { Listing, ListingItem } from '../../components/listing/v2';
import { CreateAction } from '../../components/listing/v2/ListingAction';
import {
  Activity,
  Calendar,
  BookOpen
} from 'lucide-react';

const CurriculumManagement = () => {
  const navigate = useNavigate();
  const { branch_id } = useParams();

  // Determine context based on URL path
  const currentPath = window.location.pathname;
  const isPlatformAdmin = currentPath.startsWith('/admin/system-templates');
  const isClubAdmin = currentPath.startsWith('/admin/templates');
  const isBranchAdmin = currentPath.startsWith('/branch/');
  const isCoach = currentPath.startsWith('/coach/templates');

  // Determine base path for navigation
  let basePath;
  let pageTitle;
  let pageSubtitle;
  let backPath;

  if (isPlatformAdmin) {
    basePath = '/admin/system-templates';
    pageTitle = 'TRAINING CURRICULUM';
    pageSubtitle = 'Reference library of activity and session templates for clubs to copy';
    backPath = '/admin/system-templates';
  } else if (isClubAdmin) {
    basePath = '/admin/templates';
    pageTitle = 'CLUB TEMPLATES';
    pageSubtitle = 'Manage club-level templates that can be used across all branches';
    backPath = '/admin/club';
  } else if (isBranchAdmin) {
    basePath = `/branch/${branch_id}/templates`;
    pageTitle = 'BRANCH TEMPLATES';
    pageSubtitle = 'Manage branch-level templates for training sessions';
    backPath = `/branch/${branch_id}/dashboard`;
  } else if (isCoach) {
    basePath = '/coach/templates';
    pageTitle = 'TEMPLATE LIBRARY';
    pageSubtitle = 'Browse available templates to use when creating training sessions';
    backPath = '/dashboard';
  } else {
    // Fallback
    basePath = '/admin/templates';
    pageTitle = 'TEMPLATES';
    pageSubtitle = 'Manage training templates';
    backPath = '/admin/club';
  }

  const curriculumSections = [
    {
      id: 'activities',
      title: 'Activity Templates',
      description: isPlatformAdmin
        ? 'System-level activity templates (reference library for clubs)'
        : 'Manage activity templates that can be combined into session templates',
      icon: <Activity size={48} />,
      path: `${basePath}/activities`,
      color: 'var(--color-primary)'
    },
    {
      id: 'sessions',
      title: 'Session Templates',
      description: isPlatformAdmin
        ? 'System-level session templates (reference library for clubs)'
        : 'Manage session templates composed of multiple activities',
      icon: <Calendar size={48} />,
      path: `${basePath}/sessions`,
      color: 'var(--color-secondary)'
    }
  ];

  const renderCurriculumSection = (section) => {
    return (
      <ListingItem
        key={section.id}
        title={section.title}
        subtitle={section.description}
        icon={section.icon}
        onClick={() => navigate(section.path)}
        actions={
          <div style={{ display: 'flex', gap: '8px' }}>
            <CreateAction
              size="xs"
              onClick={(e) => {
                e.stopPropagation();
                navigate(section.path);
              }}
              label="Manage"
            />
          </div>
        }
      >
        <div style={{ marginTop: '1rem' }}>
          {/* Additional content could go here */}
        </div>
      </ListingItem>
    );
  };

  return (
    <AdminPageLayout
      title={pageTitle}
      subtitle={pageSubtitle}
      backPath={backPath}
      maxWidth="1200px"
    >
      <Section
        title={isPlatformAdmin ? "SYSTEM TEMPLATE LIBRARY" : isCoach ? "AVAILABLE TEMPLATES" : "TEMPLATE MANAGEMENT"}
        icon={<BookOpen size={24} />}
      >
        <Listing
          items={curriculumSections}
          variant="list"
          renderItem={renderCurriculumSection}
          emptyMessage="No templates available"
        />
      </Section>
    </AdminPageLayout>
  );
};

export default CurriculumManagement;
